import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, Package, Tag, Layers, Image as ImageIcon, FileText, HelpCircle, Star, Inbox, Settings, LogOut, Menu, X, User, ExternalLink, Sun, Moon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSite } from "@/contexts/SiteContext";
import { useTheme } from "@/contexts/ThemeContext";
import { ADMIN } from "@/constants/testIds";

const LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/brands", label: "Brands", icon: Tag },
  { to: "/admin/categories", label: "Categories", icon: Layers },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/blogs", label: "Blogs", icon: FileText },
  { to: "/admin/faqs", label: "FAQ", icon: HelpCircle },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/account", label: "My Account", icon: User },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSite();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid={ADMIN.sidebar}
        className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-border/60 bg-card transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="h-20 flex items-center justify-between px-5 border-b border-border/60">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={settings.logo}
              alt="Hermanos"
              loading="eager"
              decoding="async"
              className="h-9 w-auto"
              onError={(e) => {
                const fallback = "https://customer-assets.emergentagent.com/job_bf956fe1-1570-4257-80a8-b41c4eebcfc0/artifacts/pp6j9f5w_LOGO.png";
                if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
              }}
            />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-serif">Hermanos</span>
              <span className="text-[0.55rem] tracking-widest uppercase text-gold font-semibold">Admin CMS</span>
            </div>
          </Link>
          <button className="lg:hidden text-foreground/70" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-5rem-4rem)]">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <l.icon size={16} strokeWidth={1.6} />
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-border/60 bg-card">
          <button
            data-testid={ADMIN.logout}
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-foreground/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut size={16} strokeWidth={1.6} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border/60 bg-card/60 backdrop-blur flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground/70" onClick={() => setOpen(true)}>
              <Menu size={20} />
            </button>
            <div>
              <p className="text-[0.65rem] tracking-widest uppercase text-gold font-semibold">Hermanos CMS</p>
              <p className="text-sm text-foreground/80 leading-none mt-1">{prettyTitle(location.pathname)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggle} aria-label="Toggle theme" className="h-9 w-9 rounded-sm border border-border flex items-center justify-center text-foreground/70 hover:text-gold hover:border-gold/60 transition-colors">
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <a href="/" target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-2 text-xs text-foreground/70 hover:text-gold transition-colors border border-border/70 h-9 px-3 rounded-sm">
              <ExternalLink size={13} /> View Website
            </a>
            <div className="flex items-center gap-2 pl-3 border-l border-border/60">
              <div className="h-8 w-8 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs font-semibold uppercase">
                {user?.email?.[0] || "A"}
              </div>
              <div className="text-right leading-tight hidden sm:block">
                <p className="text-xs text-foreground">{user?.name || "Admin"}</p>
                <p className="text-[0.65rem] text-foreground/60">{user?.email}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function prettyTitle(path) {
  const m = path.match(/\/admin\/([\w-]+)/);
  if (!m) return "Dashboard";
  const key = m[1].replace(/-/g, " ");
  return key.charAt(0).toUpperCase() + key.slice(1);
}
